import { useState, useEffect, useCallback, useRef } from 'react';
import API from '@/lib/api';
import DOMPurify from 'dompurify';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, Trash2, Mail, Pencil, Eye } from 'lucide-react';
import { toast } from 'sonner';

const inputClass = "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-[#0073ea]/50 focus:ring-[#0073ea]";

const PLACEHOLDERS = [
  { tag: '{{agent_first_name}}', label: 'Agent First Name', group: 'Agent' },
  { tag: '{{agent_name}}', label: 'Agent Full Name', group: 'Agent' },
  { tag: '{{property_street}}', label: 'Property Street', group: 'Property' },
  { tag: '{{property_address}}', label: 'Full Address', group: 'Property' },
  { tag: '{{offer_amount}}', label: 'Offer Price', group: 'Deal' },
  { tag: '{{escrow_deposit}}', label: 'Escrow Amount', group: 'Deal' },
  { tag: '{{inspection_time}}', label: 'Inspection Days', group: 'Deal' },
  { tag: '{{closing_date}}', label: 'Closing Date', group: 'Deal' },
  { tag: '{{seller_name}}', label: 'Seller Name', group: 'Other' },
  { tag: '{{buyer_name}}', label: 'Buyer Name', group: 'Other' },
  { tag: '{{lead_name}}', label: 'Lead Name', group: 'Other' },
];
const PLACEHOLDER_GROUPS = [...new Set(PLACEHOLDERS.map(p => p.group))].map(g => [g, PLACEHOLDERS.filter(p => p.group === g)]);

const defaultTemplate = `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
  <h2 style="color: #0073ea;">Offer Submission - {{property_street}}</h2>
  <p>Dear {{agent_first_name}},</p>
  <p>I hope this email finds you well. I am writing to submit an offer on behalf of my client for the property located at <strong>{{property_street}}</strong>.</p>
  <p><strong>Offer Details:</strong></p>
  <ul>
    <li>Offer Amount: <strong>{{offer_amount}}</strong></li>
    <li>Escrow Deposit: <strong>{{escrow_deposit}}</strong></li>
    <li>Inspection Period: <strong>{{inspection_time}}</strong></li>
    <li>Closing Date: <strong>{{closing_date}}</strong></li>
  </ul>
  <p>Please find the contract attached for your review. We look forward to hearing from you.</p>
  <p>Best regards</p>
</div>`;

export default function EmailTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', subject: '', html_content: '' });

  // Track which field to insert placeholders into
  const [activeField, setActiveField] = useState('html_content'); // 'subject' or 'html_content'
  const subjectRef = useRef(null);
  const contentRef = useRef(null);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await API.get('/email-templates');
      setTemplates(res.data);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const openCreate = () => {
    setEditId(null);
    setForm({ name: '', subject: '', html_content: defaultTemplate });
    setActiveField('html_content');
    setDialogOpen(true);
  };

  const openEdit = (tmpl) => {
    setEditId(tmpl.id);
    setForm({ name: tmpl.name, subject: tmpl.subject, html_content: tmpl.html_content });
    setActiveField('html_content');
    setDialogOpen(true);
  };

  const insertPlaceholder = (tag) => {
    const field = activeField;
    const ref = field === 'subject' ? subjectRef.current : contentRef.current;
    if (!ref) {
      // Fallback: append to end
      setForm(prev => ({ ...prev, [field]: prev[field] + tag }));
      return;
    }
    const start = ref.selectionStart || 0;
    const end = ref.selectionEnd || 0;
    const current = form[field];
    const newValue = current.substring(0, start) + tag + current.substring(end);
    setForm(prev => ({ ...prev, [field]: newValue }));
    // Restore cursor position after the inserted tag
    setTimeout(() => {
      ref.focus();
      const newPos = start + tag.length;
      ref.setSelectionRange(newPos, newPos);
    }, 0);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editId) {
        await API.put(`/email-templates/${editId}`, form);
        toast.success('Template updated');
      } else {
        await API.post('/email-templates', form);
        toast.success('Template created');
      }
      setDialogOpen(false);
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await API.delete(`/email-templates/${id}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch { toast.error('Failed to delete'); }
  };


  return (
    <div data-testid="email-templates-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Email Templates</h1>
          <p className="text-gray-500 text-sm mt-1">Create reusable email templates with dynamic placeholders</p>
        </div>
        <Button data-testid="create-email-template-btn" onClick={openCreate} className="bg-[#0073ea] hover:bg-[#0060c2] text-white">
          <Plus className="h-4 w-4 mr-2" /> New Template
        </Button>
      </div>

      {/* Placeholder reference card */}
      <Card className="bg-blue-50/50 border-blue-100 shadow-sm">
        <CardContent className="p-4">
          <p className="text-xs font-medium text-gray-600 mb-2">Available placeholders (click to copy):</p>
          <div className="flex flex-wrap gap-1.5">
            {PLACEHOLDERS.map(p => (
              <button
                key={p.tag}
                onClick={() => { navigator.clipboard.writeText(p.tag); toast.success(`Copied ${p.tag}`); }}
                className="text-[11px] font-mono bg-white text-[#0073ea] px-2 py-1 rounded border border-blue-200 hover:bg-blue-50 hover:border-[#0073ea]/50 transition-colors cursor-pointer"
              >
                {p.tag}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Templates */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2].map(i => <Skeleton key={i} className="h-48 bg-gray-200 rounded-xl" />)}
        </div>
      ) : templates.length === 0 ? (
        <Card className="bg-white border-gray-200 border-dashed shadow-sm">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Mail className="h-12 w-12 text-gray-300 mb-4" strokeWidth={1.5} />
            <p className="text-gray-500 text-lg font-medium">No email templates yet</p>
            <p className="text-gray-400 text-sm mt-1">Create your first email template to speed up outreach</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map(tmpl => (
            <Card key={tmpl.id} data-testid={`email-template-card-${tmpl.id}`} className="bg-white border-gray-200 hover:border-[#0073ea]/30 hover:shadow-sm transition-all duration-300">
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="h-10 w-10 rounded-lg bg-amber-50 flex items-center justify-center">
                    <Mail className="h-5 w-5 text-[#fdab3d]" strokeWidth={1.5} />
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-400 hover:text-gray-900" onClick={() => setPreviewHtml(tmpl.html_content)} data-testid={`preview-email-${tmpl.id}`}>
                      <Eye className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-400 hover:text-gray-900" onClick={() => openEdit(tmpl)} data-testid={`edit-email-${tmpl.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600 hover:bg-red-50" onClick={() => handleDelete(tmpl.id)} data-testid={`delete-email-${tmpl.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-1">{tmpl.name}</h3>
                <p className="text-xs text-gray-500">Subject: {tmpl.subject}</p>
                <p className="text-xs text-gray-400 mt-2">{new Date(tmpl.created_at).toLocaleDateString()}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="email-template-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl text-gray-900">{editId ? 'Edit' : 'Create'} Email Template</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="mt-2">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_240px] gap-5">
              {/* Left: Form fields */}
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-gray-600 text-xs uppercase tracking-wider">Template Name *</Label>
                  <Input data-testid="email-tpl-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required placeholder="e.g. Offer Submission" className={inputClass} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-gray-600 text-xs uppercase tracking-wider">Subject Line *</Label>
                  <Input
                    ref={subjectRef}
                    data-testid="email-tpl-subject"
                    value={form.subject}
                    onChange={e => setForm({ ...form, subject: e.target.value })}
                    onFocus={() => setActiveField('subject')}
                    required
                    placeholder="Offer for {{property_street}}"
                    className={`${inputClass} ${activeField === 'subject' ? 'ring-2 ring-[#0073ea]/30' : ''}`}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-gray-600 text-xs uppercase tracking-wider">HTML Content *</Label>
                  <Textarea
                    ref={contentRef}
                    data-testid="email-tpl-content"
                    value={form.html_content}
                    onChange={e => setForm({ ...form, html_content: e.target.value })}
                    onFocus={() => setActiveField('html_content')}
                    required
                    rows={16}
                    className={`${inputClass} resize-none font-mono text-xs ${activeField === 'html_content' ? 'ring-2 ring-[#0073ea]/30' : ''}`}
                  />
                </div>
                <Button type="submit" data-testid="save-email-tpl-btn" disabled={saving} className="w-full bg-[#0073ea] hover:bg-[#0060c2] text-white">
                  {saving ? 'Saving...' : (editId ? 'Update Template' : 'Create Template')}
                </Button>
              </div>

              {/* Right: Placeholder chips */}
              <div className="bg-[#f6f7fb] rounded-xl p-4 border border-gray-100 h-fit lg:sticky lg:top-0">
                <p className="text-xs font-semibold text-gray-700 mb-1">Insert Placeholder</p>
                <p className="text-[10px] text-gray-400 mb-3">
                  Click to insert at cursor in <span className="font-medium text-gray-600">{activeField === 'subject' ? 'Subject' : 'Body'}</span>
                </p>
                {PLACEHOLDER_GROUPS.map(([group, items]) => (
                  <div key={group} className="mb-3">
                    <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1.5">{group}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {items.map(p => (
                        <button
                          key={p.tag}
                          type="button"
                          data-testid={`placeholder-${p.tag.replace(/[{}]/g, '')}`}
                          onClick={() => insertPlaceholder(p.tag)}
                          className="text-[11px] bg-white text-gray-700 px-2 py-1.5 rounded-md border border-gray-200 hover:border-[#0073ea]/50 hover:text-[#0073ea] hover:bg-blue-50 transition-all cursor-pointer leading-tight"
                          title={p.tag}
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!previewHtml} onOpenChange={(open) => { if (!open) setPreviewHtml(''); }}>
        <DialogContent className="bg-white text-black max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl text-gray-900">Email Preview</DialogTitle>
          </DialogHeader>
          <div className="light-zone mt-2 rounded-lg bg-white p-4 text-gray-900" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewHtml) }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
